/**
 * El plan del P&L del cliente: los tipos y las decisiones que no son pintado.
 *
 * `account_local` es normativo —el PCGE lo aprueba el Consejo Normativo— y esto
 * es lo contrario: **no lo exige ningun fisco**. Se disena para contestar las
 * preguntas del negocio (margen bruto, EBITDA, cuanto de esto es caja) y por eso
 * cambia con el negocio. Una agencia de servicios tiene costo de ventas; una
 * licenciadora de software puede no tenerlo, y entonces el «margen bruto» que el
 * sistema calcula no significa nada para ella.
 */

export type TipoEstado = "activo" | "pasivo" | "patrimonio" | "ingreso" | "costo" | "gasto"

export interface CuentaGerencial {
  id: string
  codigo: string
  nombre: string
  nivel: number | null
  padre_codigo: string | null
  tipo_estado: TipoEstado
  activo: boolean
  /** `null` = no se declaró, y entonces se deriva del tipo. */
  ebitda_incluido: boolean | null
  orden: number | null
  origen: string | null
  /** Cuántas reglas del mapa local apuntan a esta cuenta HOY. */
  mapeos_vigentes: number
  seccion: string
  /** Lo que el motor usa: el declarado si lo hay, el derivado si no. */
  ebitda_efectivo: boolean
  ebitda_declarado: boolean
}

export interface PlanGerencial {
  cuentas: CuentaGerencial[]
  tipos: { codigo: TipoEstado; seccion: string }[]
  /** `false` = esta empresa no tiene margen bruto que calcular. */
  tiene_costo_de_ventas: boolean
  nota: string
}

/** Lo que cada tipo significa PARA EL P&L, que es la pregunta de negocio. */
export const QUE_HACE: Record<TipoEstado, string> = {
  ingreso: "Suma arriba de todo. Es la primera línea del P&L.",
  costo:
    "RESTA del margen bruto. Elegir «costo» en vez de «gasto» baja el margen bruto sin tocar el EBITDA: es la diferencia entre lo que cuesta entregar el servicio y lo que cuesta tener la empresa.",
  gasto:
    "Resta DEBAJO del margen bruto. Afecta el margen operativo y el EBITDA, y deja el margen bruto intacto.",
  activo: "No entra en el P&L: va al balance.",
  pasivo: "No entra en el P&L: va al balance.",
  patrimonio: "No entra en el P&L: va al balance.",
}

// ---------------------------------------------------------------------------
// Qué cambió, exactamente
// ---------------------------------------------------------------------------

export interface BorradorCuenta {
  nombre: string
  tipo_estado: TipoEstado
  padre_codigo: string
  /** "" = sin declarar (el motor deriva), "si" / "no" = declarado. */
  ebitda: "" | "si" | "no"
  orden: string
  activo: boolean
}

export interface CambioCuenta {
  nombre?: string
  tipo_estado?: TipoEstado
  padre_codigo?: string | null
  ebitda_incluido?: boolean | null
  orden?: number | null
  activo?: boolean
}

export function borradorDe(cuenta: CuentaGerencial): BorradorCuenta {
  return {
    nombre: cuenta.nombre,
    tipo_estado: cuenta.tipo_estado,
    padre_codigo: cuenta.padre_codigo ?? "",
    ebitda: cuenta.ebitda_incluido === null ? "" : cuenta.ebitda_incluido ? "si" : "no",
    orden: cuenta.orden === null ? "" : String(cuenta.orden),
    activo: cuenta.activo,
  }
}

/**
 * Solo lo que de verdad cambio.
 *
 * DOS VALORES QUE UNA COMPROBACION DE VERDAD/MENTIRA SE COME: `orden: 0`, que
 * es la primera posicion, y `ebitda_incluido: false`, que es «declarado que NO
 * suma» —distinto de «no se declaro»—. Escribir `if (v)` los descarta, la
 * pantalla dice que guardo y el valor se queda como estaba. No falla, y el
 * numero miente: es la razon de que esto sea una funcion con pruebas y no tres
 * lineas dentro del componente.
 *
 * El CODIGO no se envia nunca: es la clave contra la que apunta `account_map`,
 * y el backend no lo deja cambiar.
 */
export function cambiosDe(cuenta: CuentaGerencial, b: BorradorCuenta): CambioCuenta {
  const cambios: CambioCuenta = {}

  if (b.nombre.trim() !== cuenta.nombre) cambios.nombre = b.nombre.trim()
  if (b.tipo_estado !== cuenta.tipo_estado) cambios.tipo_estado = b.tipo_estado

  const padre = b.padre_codigo.trim()
  if (padre !== (cuenta.padre_codigo ?? "")) cambios.padre_codigo = padre || null

  const ebitda = b.ebitda === "" ? null : b.ebitda === "si"
  if (ebitda !== cuenta.ebitda_incluido) cambios.ebitda_incluido = ebitda

  const orden = b.orden.trim() === "" ? null : Number(b.orden)
  if (orden !== cuenta.orden && !Number.isNaN(orden as number)) cambios.orden = orden

  if (b.activo !== cuenta.activo) cambios.activo = b.activo

  return cambios
}

export function hayCambios(cambios: CambioCuenta): boolean {
  return Object.keys(cambios).length > 0
}

// ---------------------------------------------------------------------------
// Lo que hay que decir ANTES, no después
// ---------------------------------------------------------------------------

/**
 * El aviso de desactivar, CON el numero delante.
 *
 * El backend ya lo devuelve —despues de desactivar—, y eso llega tarde: el
 * listado trae `mapeos_vigentes`, asi que la consecuencia se puede decir
 * mientras todavia se puede no hacerlo.
 *
 * Desactivar no borra, y esa es toda la diferencia: una cuenta usada en un
 * periodo cerrado tiene que seguir existiendo o los estados de ese periodo
 * dejan de poder reconstruirse.
 */
export function avisoDeDesactivar(cuenta: CuentaGerencial): string | null {
  if (cuenta.mapeos_vigentes === 0) return null
  return (
    `${cuenta.mapeos_vigentes} ${cuenta.mapeos_vigentes === 1 ? "regla" : "reglas"} ` +
    `del mapa local ${cuenta.mapeos_vigentes === 1 ? "apunta" : "apuntan"} a «${cuenta.codigo}». ` +
    "Al desactivarla van a quedar resolviendo a una cuenta que ya no se ofrece: " +
    "revísalas en el mapeo antes de cerrar el período."
  )
}

/** El aviso de cambiar de tipo: el importe se muda de sección. */
export function avisoDeCambioDeSeccion(
  cuenta: CuentaGerencial,
  nuevo: TipoEstado,
  secciones: Record<string, string>,
): string | null {
  if (nuevo === cuenta.tipo_estado) return null
  const antes = secciones[cuenta.tipo_estado] ?? cuenta.tipo_estado
  const despues = secciones[nuevo] ?? nuevo
  return (
    `«${cuenta.codigo}» pasa de ${antes} a ${despues}. El importe cambia de sección ` +
    "en el P&L de todos los períodos abiertos, y el margen bruto con él."
  )
}

// ---------------------------------------------------------------------------
// Alta
// ---------------------------------------------------------------------------

export interface CuentaNueva {
  codigo: string
  nombre: string
  tipo_estado: TipoEstado | ""
  padre_codigo: string
  ebitda: "" | "si" | "no"
  orden: string
  motivo: string
}

export const CUENTA_NUEVA_VACIA: CuentaNueva = {
  codigo: "",
  nombre: "",
  tipo_estado: "",
  padre_codigo: "",
  ebitda: "",
  orden: "",
  motivo: "",
}

/**
 * Lo que se puede comprobar aqui, antes de la peticion.
 *
 * El codigo repetido y el padre inexistente los valida tambien el backend —y
 * ahi es donde cuenta—, pero decirlo mientras se escribe evita el viaje y, en
 * el caso del codigo desactivado, evita el peor mensaje posible: «ya existe»
 * sobre una cuenta que no esta en la lista que el usuario tiene delante.
 */
export function validarCuentaNueva(
  n: CuentaNueva,
  cuentas: CuentaGerencial[],
): string | null {
  const codigo = n.codigo.trim()
  if (codigo.length < 1) return "El código es obligatorio."
  if (n.nombre.trim().length < 2) return "El nombre es obligatorio."
  if (!n.tipo_estado) return "Elige el tipo: decide en qué sección del P&L entra el importe."
  if (n.motivo.trim().length < 3) return "El motivo es obligatorio."

  const choque = cuentas.find((c) => c.codigo === codigo)
  if (choque) {
    return choque.activo
      ? `El código «${codigo}» ya lo usa «${choque.nombre}».`
      : `El código «${codigo}» existe y está DESACTIVADO («${choque.nombre}»). Reactívalo en vez de crearlo: marca «ver también las desactivadas».`
  }

  const padre = n.padre_codigo.trim()
  if (padre && !cuentas.some((c) => c.codigo === padre)) {
    return `La cuenta padre «${padre}» no existe. Una cuenta colgada de un padre inexistente no aparece en el estado: el P&L se arma recorriendo el árbol.`
  }
  return null
}

/** El cuerpo del POST, con los vacíos convertidos en nulos y no en "". */
export function cuentaNuevaAApi(n: CuentaNueva) {
  return {
    codigo: n.codigo.trim(),
    nombre: n.nombre.trim(),
    tipo_estado: n.tipo_estado as TipoEstado,
    padre_codigo: n.padre_codigo.trim() || null,
    ebitda_incluido: n.ebitda === "" ? null : n.ebitda === "si",
    orden: n.orden.trim() === "" ? null : Number(n.orden),
    motivo: n.motivo.trim(),
  }
}
